import type { EntityDetail, EntityResponse, EntityStep } from '../api/client.js';

export interface FlowchartNode {
  stepId: number;
  label: string;
  isRoot: boolean;
  isQuestion: boolean;
  responseFormat: string;
  isTryAgain: boolean;
  responses: Array<{
    responseId: number;
    label: string;
    isCorrect: boolean | null;
    targetStepId: number | null;
  }>;
  nextStepId: number | null;
  error?: string;
}

export function buildFlowchart(entity: EntityDetail): FlowchartNode[] {
  const steps = entity.steps ?? [];
  const responses = entity.responses ?? [];
  const stepById = new Map<number, EntityStep>();
  const responseById = new Map<number, EntityResponse>();

  for (const step of steps) {
    const id = toNumber(step.id);
    if (id !== null) {
      stepById.set(id, step);
    }
  }
  for (const response of responses) {
    const id = toNumber(response.id);
    if (id !== null) {
      responseById.set(id, response);
    }
  }

  const rootStep = steps.find((step) => step.is_root === true);
  const startId = rootStep ? toNumber(rootStep.id) : toNumber(steps[0]?.id);
  if (startId === null) {
    return [];
  }

  const visited = new Set<number>();
  const visiting = new Set<number>();
  const ordered: FlowchartNode[] = [];

  const walk = (stepId: number) => {
    if (visited.has(stepId)) {
      return;
    }
    const step = stepById.get(stepId);
    if (!step) {
      return;
    }

    if (visiting.has(stepId)) {
      ordered.push(materializeNode(step, responseById, 'Non-try-again cycle detected'));
      return;
    }

    visiting.add(stepId);
    const node = materializeNode(step, responseById);
    ordered.push(node);

    const isTryAgain = node.isTryAgain;
    const targets = [
      node.nextStepId,
      ...node.responses.map((response) => response.targetStepId),
    ].filter((target): target is number => typeof target === 'number');

    for (const target of targets) {
      if (isTryAgain && target < stepId) {
        continue;
      }
      walk(target);
    }

    visiting.delete(stepId);
    visited.add(stepId);
  };

  walk(startId);

  for (const step of steps) {
    const id = toNumber(step.id);
    if (id !== null && !visited.has(id)) {
      ordered.push(materializeNode(step, responseById, 'Unreachable from root'));
    }
  }

  return ordered;
}

/**
 * Every response id a step offers — the union of `response_ids` and the ids inside each entry of
 * `response_groups`, which is exactly what the API's `collectStepResponseIds`
 * (`packages/api/src/content/content-validation.ts:382-390`) does.
 *
 * Reading only `response_ids` — which this file did until 2026-08-12 — makes a step that wires its
 * options through `response_groups` resolve to ZERO options. On the flowchart that showed a question
 * as a content step; on the review row's answer-wiring block it would be worse, because "no options"
 * there is indistinguishable from "this is prose", which is the reviewer's cue that there is nothing
 * to check. A silent under-count is the one failure this block must not have.
 */
export function collectStepResponseIds(step: EntityStep): number[] {
  const direct = Array.isArray(step.response_ids) ? step.response_ids : [];
  const grouped = Array.isArray(step.response_groups)
    ? step.response_groups.flatMap((group) =>
        group && typeof group === 'object' && Array.isArray(group.response_ids) ? group.response_ids : [],
      )
    : [];
  // Groups FIRST when they exist. `contentSerializer.ts` treats a response group as the options the
  // learner sees and step-level `response_ids` beside a group as fallbacks — a `catchall` branch for
  // input matching nothing. Listing the fallback first put "Other responses" above the accepted value
  // as though it were the first option (cross-model review finding 7). With no groups, `response_ids`
  // IS the option list and the order is unchanged.
  const ordered: number[] = [];
  const seen = new Set<number>();
  for (const raw of grouped.length > 0 ? [...grouped, ...direct] : direct) {
    const id = toNumber(raw);
    if (id !== null && !seen.has(id)) {
      seen.add(id);
      ordered.push(id);
    }
  }
  return ordered;
}

function materializeNode(
  step: EntityStep,
  responseById: Map<number, EntityResponse>,
  error?: string,
): FlowchartNode {
  const responseIds = collectStepResponseIds(step);

  return {
    stepId: toNumber(step.id) ?? -1,
    label: truncate(stripHtml(String(step.prompt ?? step.value ?? 'Untitled step'))),
    isRoot: step.is_root === true,
    isQuestion: responseIds.length > 0,
    responseFormat: String(step.response_format ?? 'content'),
    isTryAgain: step.is_try_again === true,
    responses: responseIds.map((responseId) => {
      const response = responseById.get(responseId);
      return {
        responseId,
        label: truncate(stripHtml(String(response?.value ?? `Response ${responseId}`)), 36),
        isCorrect: typeof response?.is_correct === 'boolean' ? response.is_correct : null,
        targetStepId: toNumber(response?.next_step_id),
      };
    }),
    nextStepId: toNumber(step.next_step_id),
    ...(error ? { error } : {}),
  };
}

// ---------------------------------------------------------------------------
// The answer wiring around ONE edited step — what the review row shows beside an `_edits` diff.
// ---------------------------------------------------------------------------

/**
 * What the edited field will say if this item executes.
 *
 * `unavailable` is not a bug — it is the honest answer when the reviewed anchor is not in the live
 * text exactly once, which is the same condition the executor refuses on.
 */
export type FieldProjection =
  | { state: 'projected'; value: string }
  /** The live text already IS the post-edit text (a resumed execution, or somebody applied it). */
  | { state: 'already_applied'; value: string }
  | { state: 'unavailable'; reason: string };

/** One answer option on the located step. */
export interface WiringResponse {
  responseId: number;
  /** The option's live text. NOT truncated — truncating the answer is the defect this block fixes. */
  text: string;
  /**
   * One entry per edited field of this response (`value`, `feedback`). A map rather than a single
   * value because the executor allows an envelope to edit both, and projecting only the first left
   * the card saying "this item edits feedback" above the OLD feedback (cross-model review finding 3).
   */
  projectedFields: Record<string, FieldProjection>;
  /** Inline feedback shown for this option. Distinct from the routed step, and often the only one. */
  feedback: string | null;
  /** `null` is a distinct claim from `false`: the document does not say. Render it as such. */
  isCorrect: boolean | null;
  targetStepId: number | null;
  targetStepText: string | null;
  /** The routed-to step is itself a question, not feedback prose — a different thing to read. */
  targetStepIsQuestion: boolean;
  /** Routes to a step id that is not in the document. */
  targetStepMissing: boolean;
  /** Fields of THIS response that the item's envelope edits (`value` / `feedback`). */
  editedFields: string[];
  /**
   * The stored `type`. `'catchall'` is the branch taken by input matching no option — it is NOT a
   * choice a learner can pick, and `contentSerializer.ts:129-136` keeps it out of the response groups
   * for exactly that reason. Listing it beside real options without saying so implies a third
   * clickable answer that does not exist (cross-model review finding 8).
   */
  responseType: string | null;
  warnings: string[];
}

/** A route INTO the located step — how a learner arrives at the prose being edited. */
export interface InboundRoute {
  fromStepId: number;
  fromStepText: string;
  responseId: number;
  responseText: string;
  isCorrect: boolean | null;
}

/** The question a prose step leads into. */
export interface ForwardQuestion {
  stepId: number;
  text: string;
  responses: Array<{ responseId: number; text: string; isCorrect: boolean | null }>;
}

export interface StepWiring {
  stepId: number;
  /** The step's live `prompt`. */
  stepText: string;
  /** The step's live `value`, when it is non-empty and says something the prompt does not. */
  stepValue: string | null;
  /** One entry per edited field of the step itself (`prompt`, `value`). See `WiringResponse`. */
  projectedFields: Record<string, FieldProjection>;
  responseFormat: string;
  isTryAgain: boolean;
  /** Fields of the step itself that the item's envelope edits (`prompt` / `value`). */
  editedFields: string[];
  /** The options this step offers. */
  responses: WiringResponse[];
  /** Populated only when the step offers no options of its own — then this is the whole relation. */
  inbound: InboundRoute[];
  /** How many routes lead here in total. Larger than `inbound.length` means the list was cut. */
  inboundTotal: number;
  /** Populated only when the step offers no options and leads straight into a question. */
  forward: ForwardQuestion | null;
  warnings: string[];
}

export type WiringBlock =
  | { kind: 'wiring'; wiring: StepWiring }
  | { kind: 'unresolved'; label: string; message: string }
  /** Something true the reviewer must not read out of a silence. */
  | { kind: 'note'; message: string };

interface EditLocatorLike {
  scope?: unknown;
  id?: unknown;
  field?: unknown;
}

interface EditEntryLike {
  locator?: EditLocatorLike;
  before?: unknown;
  after?: unknown;
  mode?: unknown;
}

/**
 * Given a live entity/task document and a work item's `_edits` envelope, return the answer
 * relationships around each step the envelope's locators name.
 *
 * ## Why "around", not "of"
 *
 * The first draft rendered a located step's own options and dropped every step that had none. A
 * cross-model review (`_codex-design-r1.answer.md`, findings 3 and 4) proved that silently discards
 * the exact defects this block exists to catch, with verified rows from the real backlog:
 *
 * - `alts-e4551-f1-drafted-107` edits **step 8**, the feedback the clawback answer routes into. Step
 *   8 has no options, so the first draft drew nothing — while the relationship that makes the edit
 *   wrong is the INBOUND one: which answer sends a learner here, and whether it is the keyed one.
 * - `alts-e1958-f0-drafted-35`, `alts-e2001-f5-drafted-53`, `alts-e4550-f1-drafted-102` each edit a
 *   prose step whose `next_step_id` leads into a question, and each gives away that question's key.
 *
 * So a step with no options of its own gets its inbound routes and its forward question instead.
 * A step with no options, no inbound route and no forward question renders nothing — that one really
 * is prose with no answer relationship.
 *
 * ## What the text says
 *
 * Unedited surroundings are LIVE. The edited field is additionally projected forward — because the
 * point is to judge the relationship the edit CREATES, and on the worked case the live stem and the
 * live feedback agree perfectly; the contradiction exists only after the substitution. The
 * projection uses the executor's own rule (the anchor must occur exactly once, counting overlapping
 * positions) and REFUSES rather than guessing, so it never invents a state the executor would not
 * produce. It is a preview, not a promise: it does not model sibling edits in the same atomic group,
 * and the caller must say so.
 *
 * Element ids resolve STRICTLY — `typeof id === 'number' && id === locator.id`, mirroring `locate()`
 * in `packages/api/src/content/edits-envelope.ts:531-592`. A coercing match would draw the wiring of
 * a step the executor will then refuse to edit. Wiring REFERENCES (`response_ids`, `next_step_id`)
 * are read leniently instead, because that is what the API and the player do with them
 * (`content-validation.ts` `asNumber`) — the strictness belongs to the locator, which is a claim
 * about identity, not to a pointer the content itself already resolves loosely.
 */
export function resolveEditWiring(
  document: { steps?: EntityStep[]; responses?: EntityResponse[] },
  patch: Record<string, unknown>,
  options: { answerGated?: boolean } = {},
): WiringBlock[] {
  const entries = patch?._edits;
  if (!Array.isArray(entries) || entries.length === 0) {
    return [];
  }

  const steps = Array.isArray(document?.steps) ? document.steps : [];
  const responses = Array.isArray(document?.responses) ? document.responses : [];

  const responseById = new Map<number, EntityResponse>();
  const duplicateResponseIds = new Set<number>();
  for (const response of responses) {
    const id = toNumber(response?.id);
    if (id === null) {
      continue;
    }
    if (responseById.has(id)) {
      duplicateResponseIds.add(id);
    } else {
      responseById.set(id, response);
    }
  }
  const stepById = new Map<number, EntityStep>();
  const duplicateStepIds = new Set<number>();
  for (const step of steps) {
    const id = toNumber(step?.id);
    if (id === null) {
      continue;
    }
    if (stepById.has(id)) {
      duplicateStepIds.add(id);
    } else {
      stepById.set(id, step);
    }
  }

  // Pass 1 — resolve every locator to step indices, and bucket the entries by the field they edit.
  const stepEdits = new Map<number, Map<string, EditEntryLike[]>>(); // step index -> field -> entries
  const responseEdits = new Map<number, Map<string, EditEntryLike[]>>(); // response id -> field -> entries
  const order: number[] = [];
  const unresolved: WiringBlock[] = [];
  const notes: WiringBlock[] = [];
  let documentScoped = 0;

  const bucket = (map: Map<number, Map<string, EditEntryLike[]>>, key: number, field: unknown, entry: EditEntryLike) => {
    if (typeof field !== 'string') {
      return;
    }
    const byField = map.get(key) ?? new Map<string, EditEntryLike[]>();
    byField.set(field, [...(byField.get(field) ?? []), entry]);
    map.set(key, byField);
  };

  for (const raw of entries) {
    const entry = (raw && typeof raw === 'object' ? raw : {}) as EditEntryLike;
    const locator = entry.locator ?? {};
    const scope = locator.scope;
    if (scope === 'document') {
      documentScoped += 1;
      continue;
    }
    const label = `${displayScalar(scope)} ${displayScalar(locator.id)} · ${displayScalar(locator.field)}`;

    if (scope === 'step') {
      const matches = strictMatches(steps, locator.id);
      const refusal = refuse(matches.length, label, 'step', locator.id);
      if (refusal) {
        unresolved.push(refusal);
        continue;
      }
      const index = matches[0]!;
      bucket(stepEdits, index, locator.field, entry);
      if (!order.includes(index)) {
        order.push(index);
      }
      continue;
    }

    if (scope === 'response') {
      const matches = strictMatches(responses, locator.id);
      const refusal = refuse(matches.length, label, 'response', locator.id);
      if (refusal) {
        unresolved.push(refusal);
        continue;
      }
      const responseId = toNumber(responses[matches[0]!]!.id)!;
      // Which step OFFERS this option? That is the step whose wiring the reviewer needs. A response
      // wired onto two steps yields both blocks — both are true, and dropping one would hide a place
      // the edited text appears.
      const owners = steps
        .map((step, index) => ({ step, index }))
        .filter(({ step }) => collectStepResponseIds(step).includes(responseId));
      if (owners.length === 0) {
        unresolved.push({
          kind: 'unresolved',
          label,
          message: `response ${responseId} is not offered by any step in this document, so there is no `
            + 'question to show around it.',
        });
        continue;
      }
      bucket(responseEdits, responseId, locator.field, entry);
      for (const { index } of owners) {
        if (!order.includes(index)) {
          order.push(index);
        }
      }
      continue;
    }

    unresolved.push({ kind: 'unresolved', label, message: `unrecognised locator scope "${String(scope)}".` });
  }

  // Fields this platform refuses to write through a reviewed edit. Mirrors the server's own
  // allow-list, where `response.value` is deliberately absent.
  const withdrawnFields = [...responseEdits.entries()]
    .filter(([, fields]) => fields.has('value') && !options.answerGated)
    .map(([responseId]) => `the text of answer option ${responseId}`);

  // Emitted whenever the envelope carries one, NOT only when it carries nothing else. A mixed
  // document+step envelope used to suppress it — and where the step had no answer relationship the
  // whole block vanished, which is the silence this note exists to prevent (cross-model review
  // finding 4).
  if (documentScoped > 0) {
    // Silence would be read as "nothing here to check". It is not: a summary can contradict a step,
    // and this block does not look. Say so rather than let absence make the claim.
    notes.push({
      kind: 'note',
      message: documentScoped === 1
        ? 'This edit changes a document-level field (title, summary or reading text). Answer wiring is '
          + 'not evaluated for those — it addresses no step.'
        : `These ${documentScoped} edits change document-level fields (title, summary or reading text). `
          + 'Answer wiring is not evaluated for those — they address no step.',
    });
  }

  // The envelope is written by ONE `updateOne` and `resolveEditsEnvelope` refuses it whole: if any
  // locator does not resolve, NOTHING is written — not even the entries that did resolve. So a
  // projection beside an unresolved card would show text that will never exist anywhere (cross-model
  // review finding 2). One flag, applied to every projection in the run.
  //
  // THE SAME IS TRUE OF A WITHDRAWN FIELD, and for a while this only refused the withdrawn entry
  // itself: an envelope editing an option's text, its feedback and its owning step's prompt showed
  // the option as unavailable while the other two projected cleanly, so the reviewer was still being
  // shown a lesson that will never read that way. The server refuses the envelope, not the entry
  // (cross-model red-team of the deploy-readiness diff, 2026-09-20).
  const envelopeRefusal: FieldProjection | null = unresolved.length > 0
    ? {
        state: 'unavailable',
        reason: 'another edit in this item names content that is not in the live document, and the '
          + 'executor applies the whole envelope or none of it — so nothing here would be written.',
      }
    : withdrawnFields.length > 0
      ? {
          state: 'unavailable',
          reason: `another edit in this item changes ${withdrawnFields[0]}, which was withdrawn from `
            + 'reviewed edits on 2026-09-18 because changing an answer option\'s wording can change which '
            + 'answer is correct while nothing in the patch says so. The executor applies the whole '
            + 'envelope or none of it, so nothing here would be written.',
        }
      : null;

  // Pass 2 — materialise each named step once.
  const blocks: WiringBlock[] = [];
  for (const index of order) {
    const step = steps[index]!;
    const stepId = toNumber(step.id) ?? -1;
    const fields = stepEdits.get(index) ?? new Map<string, EditEntryLike[]>();
    const responseIds = collectStepResponseIds(step);

    const promptRender = renderContentText(step.prompt);
    const valueRender = renderContentText(step.value);
    const headline = promptRender.text ? promptRender : valueRender;
    const editedFields = [...fields.keys()];
    // EVERY edited field of the step, not just the first. `prompt ?? value` alone can also show an
    // empty prompt and omit the field being edited (cross-model review findings 3 and 12).
    const stepProjections: Record<string, FieldProjection> = {};
    for (const field of editedFields) {
      if (field !== 'prompt' && field !== 'value') {
        continue;
      }
      stepProjections[field] = envelopeRefusal
        ?? projectField(field === 'prompt' ? step.prompt : step.value, fields.get(field)!);
    }

    const warnings = [...promptRender.warnings, ...valueRender.warnings];
    if (duplicateStepIds.has(stepId)) {
      warnings.push(`More than one step in this document carries id ${stepId}.`);
    }

    const wiringResponses: WiringResponse[] = responseIds.map((responseId) => {
      const response = responseById.get(responseId);
      const responseFields = responseEdits.get(responseId) ?? new Map<string, EditEntryLike[]>();
      const targetStepId = toNumber(response?.next_step_id);
      const target = targetStepId === null ? undefined : stepById.get(targetStepId);
      const valueText = response ? renderContentText(response.value) : { text: '', warnings: [] as string[] };
      const targetText = target ? renderStepText(target) : { text: '', warnings: [] as string[] };
      const feedbackText = response ? renderContentText(response.feedback) : { text: '', warnings: [] as string[] };
      const feedback = feedbackText.text;
      // Every warning is carried. Discarding the feedback's turned an `<img>`-only feedback into an
      // empty line with no explanation (cross-model review finding 8).
      const responseWarnings = [...valueText.warnings, ...targetText.warnings, ...feedbackText.warnings];
      if (!response) {
        responseWarnings.push(`Response ${responseId} is wired to this step but is not in the document.`);
      }
      if (duplicateResponseIds.has(responseId)) {
        responseWarnings.push(`More than one response carries id ${responseId}; showing the first.`);
      }
      // The routed-to step is looked up in a map that keeps the first of any duplicate, so a second
      // step with the same id would be shown as though it were the destination (cross-model review
      // finding 6).
      if (targetStepId !== null && duplicateStepIds.has(targetStepId)) {
        responseWarnings.push(
          `More than one step carries id ${targetStepId}; the text below is the first of them.`,
        );
      }
      return {
        responseId,
        text: response ? valueText.text : `(response ${responseId} is missing from this document)`,
        projectedFields: projectResponseFields(response, responseFields, envelopeRefusal, options.answerGated === true),
        feedback: feedback || null,
        isCorrect: typeof response?.is_correct === 'boolean' ? response.is_correct : null,
        targetStepId,
        targetStepText: target ? targetText.text : null,
        targetStepIsQuestion: target ? collectStepResponseIds(target).length > 0 : false,
        targetStepMissing: targetStepId !== null && target === undefined,
        editedFields: [...responseFields.keys()],
        responseType: typeof response?.type === 'string' ? response.type : null,
        warnings: responseWarnings,
      };
    });

    // A step with no options of its own: the answer relationship is inbound, and forward into the
    // next question. Both are silent in the first draft and both carry real, verified defects.
    let inbound: InboundRoute[] = [];
    let inboundTotal = 0;
    let forward: ForwardQuestion | null = null;
    if (responseIds.length === 0) {
      // Scans the RAW responses array, not the first-wins `responseById` map: with two responses
      // sharing an id, where only the second routes here, the map would miss the route entirely and
      // the block would vanish (cross-model review finding 6).
      for (const owner of steps) {
        const ownerIds = new Set(collectStepResponseIds(owner));
        for (const candidate of responses) {
          const candidateId = toNumber(candidate?.id);
          if (candidateId === null || !ownerIds.has(candidateId)) {
            continue;
          }
          if (toNumber(candidate.next_step_id) !== stepId) {
            continue;
          }
          const ownerText = renderStepText(owner);
          const candidateText = renderContentText(candidate.value);
          warnings.push(...ownerText.warnings, ...candidateText.warnings);
          inbound.push({
            fromStepId: toNumber(owner.id) ?? -1,
            fromStepText: ownerText.text,
            responseId: candidateId,
            responseText: candidateText.text,
            isCorrect: typeof candidate.is_correct === 'boolean' ? candidate.is_correct : null,
          });
        }
      }
      // A step reached from every option tells the reviewer nothing about the key and would drown
      // the block, so the list is capped — and `inboundTotal` carries the real number, because a
      // silent truncation is a claim that these are all of them.
      //
      // KEYED ROUTES ARE KEPT FIRST. Truncating in traversal order can drop the single
      // `is_correct: true` route behind nineteen distractors, i.e. hide the one fact the block exists
      // to reveal (cross-model review finding 5). A stable partition, so order is otherwise preserved.
      inboundTotal = inbound.length;
      inbound = [
        ...inbound.filter((route) => route.isCorrect === true),
        ...inbound.filter((route) => route.isCorrect !== true),
      ].slice(0, INBOUND_LIMIT);

      const nextId = toNumber(step.next_step_id);
      const next = nextId === null ? undefined : stepById.get(nextId);
      const nextResponseIds = next ? collectStepResponseIds(next) : [];
      if (next && nextResponseIds.length > 0) {
        const nextText = renderStepText(next);
        warnings.push(...nextText.warnings);
        forward = {
          stepId: nextId!,
          text: nextText.text,
          responses: nextResponseIds.map((responseId) => {
            const response = responseById.get(responseId);
            const optionText = response ? renderContentText(response.value) : { text: '', warnings: [] as string[] };
            warnings.push(...optionText.warnings);
            return {
              responseId,
              text: response ? optionText.text : `(response ${responseId} is missing)`,
              isCorrect: typeof response?.is_correct === 'boolean' ? response.is_correct : null,
            };
          }),
        };
      }
    }

    if (wiringResponses.length === 0 && inbound.length === 0 && forward === null) {
      continue; // genuinely prose with no answer relationship — nothing to check
    }

    blocks.push({
      kind: 'wiring',
      wiring: {
        stepId,
        stepText: headline.text,
        stepValue: valueRender.text && valueRender.text !== headline.text ? valueRender.text : null,
        projectedFields: stepProjections,
        responseFormat: String(step.response_format ?? 'content'),
        isTryAgain: step.is_try_again === true,
        editedFields,
        responses: wiringResponses,
        inbound,
        inboundTotal,
        forward,
        warnings,
      },
    });
  }

  return [...blocks, ...unresolved, ...notes];
}

const INBOUND_LIMIT = 6;

/**
 * A step's display text — `prompt` if it renders to anything, otherwise `value`.
 *
 * Chosen AFTER rendering, not by truthiness of the raw string: `prompt: '<p></p>'` is truthy and
 * renders to nothing, so `prompt || value` hid a meaningful `value` behind an empty line
 * (cross-model review finding 8).
 */
export function renderStepText(step: EntityStep): { text: string; warnings: string[] } {
  const prompt = renderContentText(step.prompt);
  if (prompt.text) {
    return prompt;
  }
  const value = renderContentText(step.value);
  return { text: value.text, warnings: [...prompt.warnings, ...value.warnings] };
}

/**
 * AN ANSWER OPTION'S OWN TEXT IS NOT PROJECTED, BECAUSE THE SERVER WILL NOT WRITE IT.
 *
 * `responses[].value` was withdrawn from the reviewed-edit allow-list on 2026-09-18. Rewriting an
 * option's text changes which answer is correct while `is_correct` is never named in the patch, so no
 * downstream guard sees a key change; the server now refuses such an entry at all three chokepoints
 * with `code: 'field_withdrawn'`. This projection did not, and the same edit run through both
 * committed implementations produced a refusal from the server and a clean before/after preview here —
 * so the reviewer could approve, on the strength of that preview, a change that can never ship.
 *
 * The refusal is stated as a POLICY, distinctly from the drift refusal above it: no re-anchoring and
 * no re-wording clears it, and telling the reviewer to re-anchor would send him after content that is
 * not the problem.
 */
const WITHDRAWN_RESPONSE_FIELD_REASON =
  'this edit rewrites an answer option\'s own text, and that field was withdrawn from reviewed edits '
  + 'on 2026-09-18: changing an option\'s wording can change which answer is correct while nothing in '
  + 'the patch says so, and no later check would catch it. The server refuses this edit, so no preview '
  + 'of it would be true. Correcting the option needs a change that states the answer key explicitly.';

/** Every allow-listed field of one response that this envelope edits, each projected forward. */
function projectResponseFields(
  response: EntityResponse | undefined,
  fields: Map<string, EditEntryLike[]>,
  refusal: FieldProjection | null,
  answerGated: boolean,
): Record<string, FieldProjection> {
  const out: Record<string, FieldProjection> = {};
  for (const [field, entries] of fields) {
    if (field !== 'value' && field !== 'feedback') {
      continue;
    }
    if (field === 'value' && !answerGated) {
      // The POLICY refusal outranks the drift refusal: the drift reason would tell the reviewer the
      // live text has moved, which is a different and here irrelevant fact. The envelope-wide refusal
      // is passed over here too — on THIS field the specific reason is the more useful one, and the
      // siblings get the envelope-wide wording that names this entry as the cause.
      out[field] = { state: 'unavailable', reason: WITHDRAWN_RESPONSE_FIELD_REASON };
      continue;
    }
    out[field] = refusal ?? projectField(response?.[field], entries);
  }
  return out;
}

/** Indices of `elements` whose `id` is the SAME NUMBER as `wanted`. No coercion — see the doc above. */
function strictMatches(elements: Array<{ id?: unknown }>, wanted: unknown): number[] {
  if (typeof wanted !== 'number') {
    return [];
  }
  const matches: number[] = [];
  for (let index = 0; index < elements.length; index += 1) {
    const element = elements[index];
    if (element && typeof element === 'object' && typeof element.id === 'number' && element.id === wanted) {
      matches.push(index);
    }
  }
  return matches;
}

function refuse(count: number, label: string, scope: string, id: unknown): WiringBlock | null {
  if (count === 1) {
    return null;
  }
  if (count === 0) {
    return {
      kind: 'unresolved',
      label,
      message: `the live document has no ${scope} with id ${String(id)}. The content this edit was `
        + 'written against has moved or is gone, so its answer wiring cannot be shown.',
    };
  }
  return {
    kind: 'unresolved',
    label,
    message: `the live document has ${count} ${scope}s with id ${String(id)}. Refusing to guess which `
      + 'one this edit means.',
  };
}

/**
 * What the field will say if this item executes — by the executor's own rule, or not at all.
 *
 * Mirrors `edits-envelope.ts`: fragment mode needs the anchor to occur **exactly once, counting
 * overlapping positions**, and whole mode needs deep equality. Where the forward trial fails, the
 * reverse one is tried, because a live value that already equals the post-state is a resumed
 * execution rather than drift — the executor accepts that case and so must a preview of it.
 *
 * It deliberately does NOT model: sibling edits in the same atomic group, `normalizeContentDocument`
 * (which the envelope does not run either), or the composition guard. Where it cannot be certain it
 * returns `unavailable` with a reason, and the caller shows that instead of a number it made up.
 */
export function projectField(liveValue: unknown, entries: EditEntryLike[]): FieldProjection {
  if (typeof liveValue !== 'string') {
    return { state: 'unavailable', reason: 'the live field is not text.' };
  }

  // Rule E11, mirrored from the executor: in fragment mode neither of `before`/`after` may contain
  // the other, because a resume then cannot tell applied from unapplied. The executor REFUSES such an
  // envelope, so previewing one would show text nobody will ever ship (cross-model review finding 2).
  for (const entry of entries) {
    const { before, after, mode } = entry;
    if (typeof before !== 'string' || typeof after !== 'string') {
      return { state: 'unavailable', reason: 'this edit does not replace text with text.' };
    }
    if (mode === 'whole') {
      continue;
    }
    if (before === '' || after.includes(before) || before.includes(after)) {
      return {
        state: 'unavailable',
        reason: 'the reviewed text and its replacement contain one another, which the executor '
          + 'refuses (it could not tell an applied edit from an unapplied one).',
      };
    }
  }

  const run = (start: string, direction: 'forward' | 'reverse'): string | null => {
    const ordered = direction === 'forward' ? entries : [...entries].reverse();
    let value = start;
    for (const entry of ordered) {
      const from = direction === 'forward' ? entry.before : entry.after;
      const to = direction === 'forward' ? entry.after : entry.before;
      if (typeof from !== 'string' || typeof to !== 'string') {
        return null;
      }
      if (entry.mode === 'whole') {
        if (value !== from) {
          return null;
        }
        value = to;
        continue;
      }
      if (from === '' || countOverlapping(value, from) !== 1) {
        return null;
      }
      // Spliced by index, NOT `String.prototype.replace`. `replace` gives `$&`, `$'`, `` $` `` and
      // `$1` special meaning inside the replacement, so an approved text containing a dollar sign
      // followed by one of those — ordinary in CFA currency prose — would render as something nobody
      // wrote and nobody will ship. Found by cross-model review of this diff.
      const at = value.indexOf(from);
      value = value.slice(0, at) + to + value.slice(at + from.length);
    }
    return value;
  };

  const forward = run(liveValue, 'forward');
  if (forward !== null) {
    // THE COMPOSITION GUARD, and it is separate from E11. A composition of individually legal entries
    // can still be applicable to its own output (`A→B`, `C→A`, `B→C` over `"A C"` yields `"C A"`, and
    // the same envelope over `"C A"` yields `"A C"`), and the executor refuses that as
    // `edits_composition_ambiguous`. Without this the preview shows `"C A"` for a write that never
    // happens.
    if (run(forward, 'forward') !== null) {
      return {
        state: 'unavailable',
        reason: 'these edits could apply to their own result, which the executor refuses as '
          + 'ambiguous — it could not tell a finished write from an unstarted one.',
      };
    }
    return { state: 'projected', value: forward };
  }

  // Reverse trial: is the live text already the post-edit text? The executor accepts that (a resumed
  // execution whose write committed) and round-trips it, so this does too.
  const reverse = run(liveValue, 'reverse');
  if (reverse !== null && run(reverse, 'forward') === liveValue) {
    return { state: 'already_applied', value: liveValue };
  }
  return {
    state: 'unavailable',
    reason: 'the reviewed text is not in the live field exactly once, so this edit cannot be previewed '
      + '— and it would be refused at execution for the same reason.',
  };
}

/** Overlapping occurrence count, as `edits-envelope.ts` counts it: positions, not disjoint matches. */
function countOverlapping(haystack: string, needle: string): number {
  let count = 0;
  let from = 0;
  for (;;) {
    const at = haystack.indexOf(needle, from);
    if (at === -1) {
      return count;
    }
    count += 1;
    from = at + 1;
  }
}

/**
 * Live text for display, plus what the display LOST.
 *
 * HTML is not rendered — this is prose out of a content database and rendering it would be an
 * injection surface on a page that never needs its markup. It is PARSED rather than regex-stripped,
 * because a regex cannot see a `>` inside a quoted attribute: `<span title="x > y">Visible</span>`
 * ends up as `y">Visible` (cross-model review finding 8). `<script>` and `<style>` are removed first,
 * so text that never reaches a learner does not become prose here; block elements and `<br>` become
 * spaces, or `<p>…total.</p><p>Next…</p>` reads as one run-on sentence.
 *
 * Stripping is still lossy in ways that change meaning — an `<img>` is an entire diagram, a `<table>`
 * a set of row/column associations, `<sup>`/`<sub>`/MathML the arithmetic — so each is named instead
 * of being flattened silently.
 */
export function renderContentText(value: unknown, { preserveParagraphs = false }: { preserveParagraphs?: boolean } = {}): { text: string; warnings: string[] } {
  const raw = stringifyContentValue(value);
  const warnings: string[] = [];
  // THREE SPELLINGS, because course 12b uses the one the first two missed. Stored lesson text is
  // Markdown (`L-1348`), and its diagrams are written `![](/api/20b/images/<id> "")` — no HTML tag
  // and no `[img:…]` marker, so this warning was silent on precisely the fields where the loss is
  // total. A reviewer then reads prose about areas A, B and C of a figure with nothing telling him a
  // figure exists. `!\[…\]` must be followed by `(` or `[` (inline or reference-style) so that an
  // ordinary Markdown link, and an exclamation that merely precedes a bracket, do not trip it.
  if (/<img\b/i.test(raw) || /\[img:[^\]]+\]/i.test(raw) || /!\[[^\]]*\]\s*[([]/.test(raw)) {
    warnings.push('Contains an image, which is not shown here.');
  }
  if (/<table\b/i.test(raw)) warnings.push('Contains a table; its rows and columns are flattened here.');
  if (/<(sup|sub)\b/i.test(raw) || /<math\b/i.test(raw) || /\$\$[\s\S]*?\$\$/.test(raw) || /\\\([\s\S]*?\\\)/.test(raw)) {
    warnings.push('Contains mathematical notation whose structure is flattened here.');
  }
  // Full-text panels name image loss without displaying Markdown image syntax or its URL.
  const display = preserveParagraphs
    ? raw.replace(/!\[([^\]]*)\]\([^()]*(?:\([^()]*\)[^()]*)*\)/g, '$1').replace(/!\[([^\]]*)\]\[[^\]]*\]/g, '$1')
    : raw;
  return { text: toPlainText(display, preserveParagraphs), warnings };
}

function stringifyContentValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value == null) return '';
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint' || typeof value === 'symbol') {
    return String(value);
  }
  try {
    return JSON.stringify(value) ?? '';
  } catch {
    return '[unserializable stored value]';
  }
}

const BLOCK_SEPARATORS = 'br,p,div,li,tr,td,th,h1,h2,h3,h4,h5,h6,blockquote,section,article';

function toPlainText(raw: string, preserveParagraphs = false): string {
  const normalize = (text: string) => preserveParagraphs
    ? text.replace(/\r\n?/g, '\n').replace(/[^\S\n]+/g, ' ').replace(/ *\n */g, '\n').replace(/\n{3,}/g, '\n\n').trim()
    : text.replace(/\s+/g, ' ').trim();
  if (!raw.includes('<') && !raw.includes('&')) {
    return normalize(raw);
  }
  if (typeof DOMParser === 'function') {
    try {
      const body = new DOMParser().parseFromString(raw, 'text/html').body;
      for (const hidden of body.querySelectorAll('script,style')) {
        hidden.remove();
      }
      for (const block of body.querySelectorAll(BLOCK_SEPARATORS)) {
        if (preserveParagraphs && block.tagName !== 'BR') block.before('\n\n');
        block.after(preserveParagraphs ? (block.tagName === 'BR' ? '\n' : '\n\n') : ' ');
      }
      return normalize(body.textContent ?? '');
    } catch {
      // fall through to the regex path
    }
  }
  // Fallback only. The name character after `<` is what keeps `0 < r > -1` intact.
  return normalize(decodeEntities(raw.replace(/<!--[\s\S]*?-->/g, ' ').replace(/<\/?[a-zA-Z][^>]*>/g, preserveParagraphs ? '\n' : ' ')));
}

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
  rsquo: '’', lsquo: '‘', rdquo: '”', ldquo: '“',
  ndash: '–', mdash: '—', hellip: '…',
};

function decodeEntities(value: string): string {
  return value.replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z]+);/g, (match, body: string) => {
    if (body.startsWith('#')) {
      const code = body[1] === 'x' || body[1] === 'X' ? Number.parseInt(body.slice(2), 16) : Number.parseInt(body.slice(1), 10);
      return Number.isFinite(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : match;
    }
    return NAMED_ENTITIES[body.toLowerCase()] ?? match;
  });
}

/**
 * Strip markup for the FLOWCHART's truncated labels — but only what actually looks like a tag.
 *
 * The old pattern `<[^>]+>` swallows ordinary prose: `0 < r > -1` matches it end to end and the
 * comparison silently disappears. Requiring a name character (or a closing slash) after the `<` keeps
 * real tags stripped and leaves arithmetic alone. The review block uses `toPlainText` instead, which
 * parses rather than pattern-matches; this one stays cheap because its output is truncated to 36–60
 * characters anyway.
 */
function stripHtml(value: string) {
  return value.replace(/<\/?[a-zA-Z][^>]*>/g, ' ').replace(/<!--[\s\S]*?-->/g, ' ').replace(/\s+/g, ' ').trim();
}

function truncate(value: string, max = 60) {
  return value.length > max ? `${value.slice(0, max - 1)}...` : value;
}

export function toNumber(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
}

function displayScalar(value: unknown): string {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }
  return '?';
}
